// The document store (plan §6.1): the current picture of a document, an undo
// stack of operation inverses, and one outlet through which every local
// operation leaves for the outbox. Remote operations come in through a
// separate door and never touch the undo stack.

import { type DocState, type Op, applyOp, invert } from "./ops";

const COALESCE_MS = 1000;

interface Entry {
  op: Op;
  inverse: Op;
  at: number;
}

type Listener = () => void;

export class DocumentStore {
  private listeners = new Set<Listener>();
  private undoStack: Entry[][] = [];
  private redoStack: Entry[][] = [];

  constructor(
    public doc: DocState,
    private readonly emit: (op: Op) => void,
  ) {}

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): DocState => this.doc;

  /** A local change. Applied, recorded for undo, and sent on. */
  dispatch(op: Op): void {
    const before = this.doc;
    const next = applyOp(before, op);
    if (next === before) return;
    const entry: Entry = { op, inverse: invert(before, op), at: Date.now() };
    this.doc = next;
    this.redoStack = [];
    this.record(entry);
    this.emit(op);
    this.notify();
  }

  /** Several operations that undo together, such as a multi-block paste. */
  dispatchAll(ops: Op[]): void {
    const entries: Entry[] = [];
    for (const op of ops) {
      const before = this.doc;
      const next = applyOp(before, op);
      if (next === before) continue;
      entries.push({ op, inverse: invert(before, op), at: Date.now() });
      this.doc = next;
      this.emit(op);
    }
    if (entries.length === 0) return;
    this.redoStack = [];
    this.undoStack.push(entries);
    this.notify();
  }

  private record(entry: Entry): void {
    // Consecutive typing in one paragraph is one undo step, not one per key.
    const last = this.undoStack[this.undoStack.length - 1];
    const tail = last?.[last.length - 1];
    if (
      last &&
      tail &&
      last.length === 1 &&
      tail.op.t === "set_text" &&
      entry.op.t === "set_text" &&
      tail.op.id === entry.op.id &&
      entry.at - tail.at < COALESCE_MS
    ) {
      last[0] = { op: entry.op, inverse: tail.inverse, at: entry.at };
      return;
    }
    this.undoStack.push([entry]);
  }

  undo(): boolean {
    const batch = this.undoStack.pop();
    if (!batch) return false;
    const redo: Entry[] = [];
    for (const entry of [...batch].reverse()) {
      const before = this.doc;
      this.doc = applyOp(before, entry.inverse);
      redo.push({ op: entry.inverse, inverse: invert(before, entry.inverse), at: Date.now() });
      this.emit(entry.inverse);
    }
    this.redoStack.push(redo);
    this.notify();
    return true;
  }

  redo(): boolean {
    const batch = this.redoStack.pop();
    if (!batch) return false;
    const undo: Entry[] = [];
    for (const entry of [...batch].reverse()) {
      const before = this.doc;
      this.doc = applyOp(before, entry.inverse);
      undo.push({ op: entry.inverse, inverse: invert(before, entry.inverse), at: Date.now() });
      this.emit(entry.inverse);
    }
    this.undoStack.push(undo);
    this.notify();
    return true;
  }

  /** An operation from another device. Applied without undo or re-sending. */
  applyRemote(op: Op): void {
    try {
      const next = applyOp(this.doc, op);
      if (next === this.doc) return;
      this.doc = next;
      this.notify();
    } catch {
      // It did not fit our picture; the caller reloads from the server.
      throw new Error("remote operation did not apply");
    }
  }

  /** Replace everything with the server's picture, dropping history. */
  reset(doc: DocState): void {
    this.doc = doc;
    this.undoStack = [];
    this.redoStack = [];
    this.notify();
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }
}
